import type { StoryObj } from '@storybook/html'
import type { CobaltSelect as CobaltSelectProps } from './cobalt-select'

export default {
  title: 'Components/Form/cobalt-select',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which accompany the select. It is **required** for accessibility concerns.'
    },
    placeholder: {
      type: { name: 'string', required: true },
      description: 'Placeholder inside the select.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'Select an option' }
      }
    },
    format: {
      type: { name: 'string', required: true },
      options: ['inside', 'outside', 'aside', 'hidden'],
      control: { type: 'select' },
      description: 'Position the label in different place.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'inside' }
      }
    },
    options: {
      type: { name: 'array', required: true },
      description: 'Array of objects describing the options. The array could be a simple list of options or splitted in multiple section. It should be in strict JSON syntax.',
      table: {
        type: { summary: 'JSON' }
      },
      control: { type: 'array' }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the Select field. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    requirement: {
      type: { name: 'string', required: true },
      options: ['mandatory', 'optional', 'none'],
      control: { type: 'select' },
      description: 'Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'mandatory' }
      }
    },
    cobaltSelectOptionChange: {
      description: 'Custom event emitted when select option change',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltSelectOptionChange={handleChange}\nVue:@cobaltSelectOptionChange="handleChange"\nAngular:(cobaltSelectOptionChange)="handleChange()"\nVanillaJs:.addEventListener("cobaltSelectOptionChange", (event) => {//handle change});'
        }
      },

      action: 'cobaltSelectOptionChange'
    }
  }
}

const optionsList = [
  { value: 'Value1' },
  { value: 'Value2' },
  { value: 'Value3' },
  { value: 'Value4', disabled: true },
  { value: 'Value5' },
  { value: 'Value6' },
  { value: 'Value7' },
  { value: 'Value8' },
  { value: 'Value9' },
  { value: 'Value10' }
]

const optionsListWithSection = [
  {
    title: 'Section1',
    options: [{ value: 'Value1' }, { value: 'Value2' }, { value: 'Value3' }, { value: 'Value4', disabled: true }, { value: 'Value5' }, { value: 'Value6' }]
  },
  {
    title: 'Section2',
    options: [{ value: 'Value7' }, { value: 'Value8' }, { value: 'Value9' }, { value: 'Value10', disabled: true }]
  }
]

const Template = (args: CobaltSelectProps) => {
  return `
  <cobalt-select options='${JSON.stringify(args.options)}' label="${args.label}" requirement="${args.requirement}" format="${args.format}" disabled="${args.disabled}"></cobalt-select>
</div>
`
}

export const Default: StoryObj = Template.bind({})

Default.args = {
  label: 'Select Label',
  options: optionsList,
  format: 'inside',
  requirement: 'mandatory',
  disabled: false
}

const TemplateWithSection = (args: CobaltSelectProps) => {
  return `
<cobalt-select options='${args.options}' label="${args.label}"></cobalt-select>
`
}

export const WithSection: StoryObj = TemplateWithSection.bind({})

WithSection.args = {
  label: 'Select Label',
  options: JSON.stringify(optionsListWithSection)
}

const TemplateDescription = (args: CobaltSelectProps) => {
  return `
<cobalt-select options='${args.options}' label="${args.label}" format="outside" description="${args.description}"></cobalt-select>
`
}

export const WithDescription: StoryObj = TemplateDescription.bind({})

WithDescription.args = {
  label: 'Select Label',
  options: JSON.stringify(optionsList),
  description: 'A little description which helps understand what the select can do.'
}

const TemplateCustomRequirement = (args: CobaltSelectProps) => {
  return `
<style>
  .custom-requirement {
    padding-left: var(--number-spacing-4xsmall);
  }
</style>
<cobalt-select options='${args.options}' label="${args.label}" requirement="${args.requirement}" format="${args.format}">
  <span slot="custom-requirement" class="custom-requirement">
    <core-tooltip>
      <span slot="tooltip-content">Tooltip message</span>
    </core-tooltip>
  </span>
</cobalt-select>
`
}

export const WithCustomRequirement: StoryObj = TemplateCustomRequirement.bind({})

WithCustomRequirement.args = {
  label: 'Select Label',
  options: JSON.stringify(optionsList),
  format: 'inside',
  requirement: 'none'
}

const TemplateWithErrorMessage = (args: CobaltSelectProps) => {
  return `
<script>
  document.querySelector('#toggle-button').addEventListener('cobaltButtonClick', (e) => {
    if (document.querySelector('#select-in-error').hasAttribute('error-message')) {
      document.querySelector('#select-in-error').removeAttribute('error-message')
    } else {
      document.querySelector('#select-in-error').setAttribute('error-message', 'This field is required.')
    }
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <cobalt-button id="toggle-button" label="Toggle Error State"></cobalt-button>
  <cobalt-select id="select-in-error" options='${args.options}' label="${args.label}"></cobalt-select>
</div>
`
}

export const WithErrorMessage: StoryObj = TemplateWithErrorMessage.bind({})

WithErrorMessage.args = {
  label: 'Select Label',
  options: JSON.stringify(optionsList)
}

const TemplateOnChangeEvent = (args: CobaltSelectProps) => {
  return `
  <script>
  document.querySelector('#select-on-change').addEventListener('cobaltSelectOptionChange', (e) => {
    document.querySelector('#selected-option').textContent = e.detail
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Selected option is: <span id="selected-option">not defined yet</span></p>
  <cobalt-select id="select-on-change" options='${args.options}' label="${args.label}"></cobalt-select>
</div>
`
}

export const OnChangeEvent: StoryObj = TemplateOnChangeEvent.bind({})

OnChangeEvent.args = {
  label: 'Select Label',
  options: JSON.stringify(optionsList)
}
